import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { MenuItem, ProductCategory } from '@velasca/types'

export interface Category {
  name: string
  title: string
  description: string
  hasShoes: boolean | null
  /**
   * Do not confuse this with the metafield `is_main_collection`. They do different things and serve different purposes.
   *
   * `isContainerCategory` is used to determine if the current category has children categories. Meaning that it's a level 1 or level 2 category.
   * `is_main_collection` is currently used exclusively in the breadcrumbs. It is a collection metafield set manually by the OSM team in the Shopify admin.
   */
  isContainerCategory: boolean
  children: MenuItem[]
  isMainCollection: boolean | null
  isSubCollection: boolean | null
  productCategory: ProductCategory | null
}

interface CategoryStore {
  currentCategory: Category
  setCurrentCategoryName: (currentCategoryName: string) => void
  setCurrentCategoryTitle: (currentCategoryTitle: string) => void
  setCurrentCategoryDescription: (currentCategoryDescription: string) => void
  setCurrentCategoryHasShoes: (currentCategoryHasShoes: boolean) => void
  setCurrentCategoryChildren: (currentCategoryChildren: MenuItem[]) => void
  setCurrentCategoryProductCategory: (
    currentCategoryProductCategory: ProductCategory,
  ) => void
  setCurrentCategoryIsContainerCategory: (
    currentCategoryIsContainerCategory: boolean,
  ) => void
  setCurrentCategoryIsMainCollection: (
    currentCategoryIsMainCollection: boolean,
  ) => void
  setCurrentCategoryIsSubCollection: (
    currentCategoryIsSubCollection: boolean,
  ) => void
}

export const useCategoryStore = create<CategoryStore>()(
  devtools(
    (set) => {
      return {
        currentCategory: {
          name: '',
          title: '',
          hasShoes: null,
          isContainerCategory: false,
          children: [],
          isMainCollection: null,
          isSubCollection: null,
          description: '',
          productCategory: null,
        },
        setCurrentCategoryName: (name) => {
          set((state) => {
            return {
              ...state,
              currentCategory: {
                ...state.currentCategory,
                name,
              },
            }
          })
        },
        setCurrentCategoryTitle: (title) => {
          set((state) => {
            return {
              ...state,
              currentCategory: {
                ...state.currentCategory,
                title,
              },
            }
          })
        },
        setCurrentCategoryDescription: (description) => {
          set((state) => {
            return {
              ...state,
              currentCategory: {
                ...state.currentCategory,
                description,
              },
            }
          })
        },
        setCurrentCategoryHasShoes: (hasShoes) => {
          set((state) => {
            return {
              ...state,
              currentCategory: {
                ...state.currentCategory,
                hasShoes,
              },
            }
          })
        },
        setCurrentCategoryChildren: (children) => {
          set((state) => {
            return {
              ...state,
              currentCategory: {
                ...state.currentCategory,
                children,
              },
            }
          })
        },
        setCurrentCategoryIsContainerCategory: (
          isContainerCategory: boolean,
        ) => {
          set((state) => {
            return {
              ...state,
              currentCategory: {
                ...state.currentCategory,
                isContainerCategory,
              },
            }
          })
        },
        setCurrentCategoryIsMainCollection: (isMainCollection: boolean) => {
          set((state) => {
            return {
              ...state,
              currentCategory: { ...state.currentCategory, isMainCollection },
            }
          })
        },
        setCurrentCategoryIsSubCollection: (isSubCollection: boolean) => {
          set((state) => {
            return {
              ...state,
              currentCategory: { ...state.currentCategory, isSubCollection },
            }
          })
        },
        setCurrentCategoryProductCategory: (
          productCategory: ProductCategory,
        ) => {
          set((state) => {
            return {
              ...state,
              currentCategory: { ...state.currentCategory, productCategory },
            }
          })
        },
      }
    },
    {
      name: 'Category Store',
    },
  ),
)
